import React, {
  BaseSyntheticEvent,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { CheckboxGroup } from '@uhg-abyss/web/ui/CheckboxGroup';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
const SelectInputMulti = SelectInput;
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import {
  FindhelpProgram,
  FindhelpReferralRequest,
  UniteUSReferralRequest,
  UserInfo,
} from '@src/api.types';
import {
  ReferralFormConsentText,
  AdditonalInfoTextForLogReferral,
} from './ConsentText';
import { API } from '@src/api';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import {
  filterOutPrimarySelected,
  formDataToFindhelpReferralRequest,
  findhelpServiceTagsIntoNeedSelectOptions,
  findhelpServiceTagsIntoNeedSelectOptionsforUniteus,
  formDataToUniteUsReferralRequest,
  ReferralFormData,
  SelectOption
} from './helpers';
import { getFeatureFlagsFromStorage } from '@src/utills/storage';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { UniteUsProgram } from '@repo/ui';
import { MemberInContext } from '@src/global-state';

export type ReferralFormModalProps = {
  vendor: 'findhelp' | 'uniteus';
  referringTo: FindhelpProgram | UniteUsProgram;
  onClose: () => void;
  setReferResponse: (value: any) => void;
  memberInContext: MemberInContext | null;
  paCoalitionConsent: boolean;
  loggedInAdvocate: UserInfo;
  zipcode?: string;
};

export const ReferralFormModal = ({
  vendor,
  referringTo,
  onClose,
  setReferResponse,
  memberInContext,
  paCoalitionConsent,
  loggedInAdvocate,
  zipcode,
}: ReferralFormModalProps) => {
  const [showConsentPermission, setshowConsentPermission] = useState(false);
  const [isConsent, setIsConsent] = useState(true);
  const featureFlags = getFeatureFlagsFromStorage();
  // we are showing the confirm permission checkbox to below secure role.
  const secureRole =
    'Member Services - Med Supp - National - Clinical 24_7 Nurses using ICUE';

  const [isLoading, setIsLoading] = React.useState(false);
  const [isAcceptingReferral] = useState(
    vendor === 'findhelp' ? (referringTo as FindhelpProgram).accepts_referrals : false
  );
  const [isAcceptingReferralforuniteus] = useState(
    vendor === 'uniteus' ? (referringTo as UniteUsProgram).document.receiving_referrals : null
  );
  const needSelectOptions: SelectOption[] = useMemo(() => {
    return vendor === 'findhelp'
      ? findhelpServiceTagsIntoNeedSelectOptions((referringTo as FindhelpProgram).service_tags)
      : findhelpServiceTagsIntoNeedSelectOptionsforUniteus(
          ((referringTo as UniteUsProgram).relationships.services.data ?? []).map(
            (service: { label: string; id: string }) => ({
              label: service.label,
              id: service.id,
            })
          )
        );
  }, [referringTo, vendor]);

  // population_specializations
  useEffect(() => {
    setshowConsentPermission(
      loggedInAdvocate?.roles.includes(secureRole) || false
    );
  }, [loggedInAdvocate]);

  useEffect(() => {
    if (!isAcceptingReferral)
      referralForm.setValue('contactPreferences', ['do not contact']);
  }, [isAcceptingReferral]);
  const referralForm = useForm<ReferralFormData>({
    defaultValues: {
      advocateFirstName: loggedInAdvocate?.first_name || '',
      advocateLastName: loggedInAdvocate?.last_name || '',
      advocateEmail: loggedInAdvocate?.email || '',
      memberEmail: memberInContext?.email || '',
      memberFirstName: memberInContext?.first_name || '',
      memberLastName:
        (memberInContext?.last_name ?? '')
          .split(/(?<=\s|-)/)
          .map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1);
          })
          .join('') || '',
      memberPhone: memberInContext?.phone || '',
      contactPreferences: [],
      selectedNeeds: {
        primaryNeeds: [],
        secondaryNeeds: [],
      },
      comment: '',
      consent: false,
      ConfirmPermission: false,
      pennsylvaniaCoalitionConsent: null,
    },
  });
  const [initialCheck, setinitialCheck] = useState(true);
  const handleAcknowledgementValidation = () => {
    if (!referralForm.getValues('consent')) {
      referralForm.setValue('consent', false);
      setinitialCheck(true);
      referralForm.clearErrors('consent');
      return;
    }
    referralForm.trigger('consent');
    referralForm.clearErrors('ConfirmPermission');
    setinitialCheck(false);
    setIsConsent(true);
    referralForm.setValue('consent', true);
    referralForm.setValue('ConfirmPermission', false);
  };
  const handleAcknowledgementValidationConfirmPermission = () => {
    if (!referralForm.getValues('ConfirmPermission')) {
      referralForm.setValue('ConfirmPermission', false);
      setinitialCheck(true);
      referralForm.clearErrors('ConfirmPermission');
      return;
    }
    referralForm.trigger('ConfirmPermission');
    referralForm.clearErrors('consent');
    setinitialCheck(false);
    setIsConsent(false);
    referralForm.setValue('ConfirmPermission', true);
    referralForm.setValue('consent', false);
  };

  async function onSubmit(data: ReferralFormData): Promise<any> {
    if (data.ConfirmPermission) {
      data.ConfirmPermission = undefined;
      data.consent = true;
    }
    if (vendor == 'findhelp') {
      const req = formDataToFindhelpReferralRequest(
        data,
        referringTo as FindhelpProgram,
        memberInContext!,
        loggedInAdvocate!
      );
      req.isAcceptingReferral=isAcceptingReferral||false;
      setIsLoading(true);
      const resp = await API.submitReferralFindhelp(
        req as FindhelpReferralRequest
      );
      if (resp !== null && resp !== undefined) {
        setReferResponse(resp);
        if (resp?.status?.toLowerCase() === 'success') {
          // Successful referral closes this modal
          onClose();
        }
      } else {
        setReferResponse(resp);
      }
    } else if (vendor == 'uniteus') {
      const req = formDataToUniteUsReferralRequest(
        data,
        referringTo as UniteUsProgram,
        memberInContext!,
        loggedInAdvocate!,
        needSelectOptions,
        zipcode ?? memberInContext?.zipCode ?? ''
      );
      req.isAcceptingReferral=isAcceptingReferralforuniteus||false;
      setIsLoading(true);
      const resp = await API.submitReferralUniteUS(
        req as UniteUSReferralRequest
      );
      if (resp !== null && resp !== undefined) {
        setReferResponse(resp);
        if (resp?.status?.toLowerCase() === 'success') {
          onClose();
        }
      } else {
        setReferResponse(resp);
      }
    }

    setIsLoading(false);
  }

  return (
    <React.Fragment>
      <ModalDialog
        header={
          <div style={{ paddingLeft: '16px', paddingRight: '16px', paddingTop: '16px' }}>
            <span style={{ fontFamily: 'Georgia, serif', fontSize: '2rem' }}>Next Steps</span>
          </div>
        }
        isOpen={true}
        onClose={onClose}
        css={{
          'abyss-modal-content-container': {
            padding: '8px',
            maxHeight: '80vh',
          },
          'abyss-modal-base-header-container': {
            borderBottom: 'none',
          },
          'abyss-modal-base-body': {
            overflowY: 'auto',
            maxHeight: 'calc(80vh - 80px)',
          },
        }}
      >
        <FormProvider state={referralForm} onSubmit={onSubmit}>
          <div style={{ padding: '0 16px' }}>
          {/* Program info */}
          <ReferralFormProgramInfoSection
            referralForm={referralForm}
            referringTo={referringTo}
            vendor={vendor}
          />
          <PaddedLineBreak />

          {/* Advocate's information (form fields which are not editable) */}
          <ReferralFormAdvocateInfoSection
            referralForm={referralForm}
            vendor={vendor}
          />
          <PaddedLineBreak />

          {/* Member's info */}

          <ReferralFormMemberInfoSection
            referralForm={referralForm}
            vendor={vendor}
            needSelectOptions={needSelectOptions}
          />
          <PaddedLineBreak />

          {/* Member contact preferences */}
          {isAcceptingReferral ? (
            <ReferralFormMemberContactPreferences referralForm={referralForm} />
          ) : (
            ''
          )}
          {vendor === 'uniteus'&& isAcceptingReferralforuniteus ? (
            <ReferralFormMemberContactPreferences_UniteUS
              referralForm={referralForm}
            />
          ) : (
            ''
          )}

          {isAcceptingReferral ? (
            <div>
              <TextInputArea
                label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Notes</span>}
                maxLength={750}
                placeholder="Notes"
                model="comment"
                css={{ 'abyss-text-input-area': { fontFamily: 'inherit', height: '80px', minHeight: '80px' } }}
              />
              <ul style={{ fontSize: '13px', color: '#323334', marginLeft: '20px', marginTop: '8px', listStyleType: 'disc' }}>
                <li>These notes will be sent to the CBO.</li>
                <li>These notes will not be stored within Community Connector.</li>
              </ul>
            </div>
          ) : (
            ''
          )}
          {isAcceptingReferralforuniteus && vendor === 'uniteus' ? (
            <div>
              <TextInputArea
                label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Notes</span>}
                maxLength={750}
                placeholder="Notes"
                model="comment"
                onBlur={() => referralForm.trigger('comment')}
                validators={{ required: 'Notes are required' }}
                css={{ 'abyss-text-input-area': { fontFamily: 'inherit', height: '80px', minHeight: '80px' } }}
              />
              <ul style={{ fontSize: '13px', color: '#323334', marginLeft: '20px', marginTop: '8px', listStyleType: 'disc' }}>
                <li>These notes will be sent to the CBO.</li>
                <li>These notes will not be stored within Community Connector.</li>
              </ul>
            </div>
          ) : (
            ''
          )}

          {isAcceptingReferral || isAcceptingReferralforuniteus ? (
            ''
          ) : (
            <AdditonalInfoTextForLogReferral />
          )}

          {paCoalitionConsent &&
            memberInContext?.address?.state === 'PA' &&
            memberInContext.lineOfBusiness == 'C&S' && (
              <React.Fragment>
                <div>
                  <p>
                    <p style={{ textAlign: 'justify' }}>
                      Do you give consent for PA Navigate to collect and store
                      this profile information on behalf of the PA Department of
                      Human Services (DHS)? By consenting, you agree that your
                      data may be shared with DHS, your health plan, your
                      medical providers, and community-based resources receiving
                      your referrals for the purposes of medical and non-medical
                      supportive care. Additionally, you agree to DHS's Notice
                      of Privacy Practices and PA Navigate's Terms and Privacy
                      Practices. If you would like to opt out of sharing your
                      information with the coalition after providing consent,
                      you may do so at any time. If you opt out, only the
                      referring organizations and community-based resources that
                      received previously placed referrals will continue to have
                      access to information related to those referrals. Future
                      referrals will only be shared between the referring
                      organization and the receiving community-based
                      organization. DHS will receive referral data on all PA
                      Medicaid members for reporting purposes only.
                    </p>
                  </p>
                  <RadioGroup
                    label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>How to Opt-Out:</span>}
                    model="pennsylvaniaCoalitionConsent"
                    validators={
                      memberInContext.lineOfBusiness === 'C&S' &&
                      memberInContext?.address?.state === 'PA'
                        ? { required: 'Required' }
                        : { required: false }
                    }
                  >
                    <RadioGroup.Column>
                    <p style={{ textAlign: 'justify' }}>
                        Reach out to their MCO or a coalition participating
                        provider (such as someone that has assisted with
                        referrals in the past) to request they opt the member
                        out of the coalition agreement.
                    </p>
                    <RadioGroup.Radio
                      label="Yes"
                      value="true"
                      data-cy="pa-yes"
                    />
                    <RadioGroup.Radio
                      label="No"
                      value="false"
                      data-cy="pa-no"
                    />
                    </RadioGroup.Column>
                  </RadioGroup>
                </div>{' '}
                <br />
              </React.Fragment>
            )}

          <div>
            <div style={{ marginBottom: '16px', marginTop: '12px' }}>
              <Checkbox
                label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Confirm consent</span>}
                model="consent"
                onChange={handleAcknowledgementValidation}
                validators={{
                  required: isConsent || initialCheck ? 'Required' : '',
                }}
              />
            </div>

            <div
              style={{
                marginTop: '8px',
              }}
            >
              <ReferralFormConsentText
                vendor={vendor as 'findhelp' | 'uniteus'}
                acceptsReferrals={
                  vendor === 'findhelp'
                    ? (referringTo as FindhelpProgram).accepts_referrals
                    : false
                }
              />
            </div>

            {showConsentPermission &&
            featureFlags?.agent_consent_language_options_for_med_sup
              ?.enabled ? (
              <div>
                <PaddedLineBreak />
                <div className="paddingTop10px">
                  <div style={{ marginBottom: '16px', paddingTop: '19px' }}>
                    <Checkbox
                      label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>AARP Med Supp Confirm consent</span>}
                      model="ConfirmPermission"
                      onChange={(_e: BaseSyntheticEvent) => {
                        handleAcknowledgementValidationConfirmPermission();
                      }}
                      validators={{
                        required: !isConsent || initialCheck ? 'Required' : '',
                      }}
                    />
                  </div>
                  <div className="paddingTop10px">
                    <Text>
                      I have verbal consent from the member (or the member’s
                      personal representative) to research community resources
                      and record referral details in the system so that UHC can
                      follow up with the member.
                    </Text>
                  </div>
                </div>
              </div>
            ) : (
              ''
            )}
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '16px',
              }}
            >
              <Button
                data-cy="refer-button"
                isDisabled={!referralForm.formState.isValid}
                type="submit"
              >
                Refer
              </Button>
            </div>
          </div>
          </div>
        </FormProvider>
        {isLoading ? (
          <div
            style={{
              zIndex: 999,
              backgroundColor: '#FFFFFF',
              opacity: 0.75,
              position: 'fixed',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              margin: 0,
              top: 0,
              left: 0,
            }}
          >
            <LoadingSpinner
              size="xl"
              isLoading={isLoading}
              ariaLoadingLabel="Referring...."
            />
          </div>
        ) : (
          ''
        )}
      </ModalDialog>
    </React.Fragment>
  );
};

const ReferralFormProgramInfoSection = ({
  referralForm,
  referringTo,
  vendor,
}) => {
  let closestLocation = '';
  if (vendor === 'uniteus') {
    closestLocation =
      referringTo?.document?.locations.find((location) => {
        return location.is_closest;
      }) || referringTo?.document?.locations[0];
  }
  return (
    <FormProvider state={referralForm}>
      <div>
        <Heading
          color="$core.color.brand.60"
          data-cy="referral-form-program-name"
          size="sm"
        >
          {vendor == 'uniteus' ? referringTo.document.name : referringTo.name}
        </Heading>
        {(() => {
          const providerName = vendor == 'uniteus'
            ? (referringTo as UniteUsProgram).relationships.provider?.data?.document?.name
            : (referringTo as FindhelpProgram).provider_name;
          return providerName ? (
            <Text data-cy="referral-form-program-provider-name">
              <span>by </span>
              <span style={{ color: '#196ECF' }}>{providerName}</span>
            </Text>
          ) : null;
        })()}
      </div>
    </FormProvider>
  );
};

const ReferralFormAdvocateInfoSection = ({
  referralForm,
  vendor,
}: {
  referralForm: ReturnType<typeof useForm<ReferralFormData>>;
  vendor: 'findhelp' | 'uniteus';
}) => {
  return (
    <div>
      <Layout.Group grow>
        <TextInput
          label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.60' }}>Your First Name</span>}
          model="advocateFirstName"
          validators={{ required: vendor=='uniteus' ? false : true }}
          isDisabled
        />

        <TextInput
          label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.60' }}>Your Last Name</span>}
          model="advocateLastName"
          validators={{ required: vendor=='uniteus' ? false : true }}
          isDisabled
        />
      </Layout.Group>

      <Layout.Group
        grow
        style={{
          paddingTop: '4px',
        }}
      >
        <TextInput
          label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.60' }}>E-mail</span>}
          model="advocateEmail"
          validators={{ required: vendor=='uniteus' ? false : true }}
          isDisabled
          data-cy="referral-form-advocate-email"
        />
      </Layout.Group>
    </div>
  );
};

const ReferralFormMemberInfoSection = ({
  referralForm,
  vendor,
  needSelectOptions,
}: {
  referralForm: ReturnType<typeof useForm<ReferralFormData>>;
  vendor: 'findhelp' | 'uniteus';
  needSelectOptions: SelectOption[];
}) => {
  const handleFirstNameValidation = () => {
    referralForm.trigger('memberFirstName');
  };
  const handleLastNameValidation = () => {
    referralForm.trigger('memberLastName');
  };
  const handleEmailAddressValidation = () => {
    referralForm.trigger('memberEmail');
  };
  const handlePhoneNumberValidation = () => {
    referralForm.trigger('memberPhone');
  };
  const handlePrimaryReasonValidation = () => {
    referralForm.trigger('selectedNeeds.primaryNeeds');
  };
  const handlePrimaryReasonChange = () => {
    referralForm.clearErrors('selectedNeeds.primaryNeeds');
  };
  return (
    <FormProvider state={referralForm}>
      <div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <TextInput
              label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Member's First Name</span>}
              model="memberFirstName"
              onChange={handleFirstNameValidation}
              onBlur={handleFirstNameValidation}
              isDisabled={vendor=='uniteus' ? true : false}
              validators={{ required: vendor=='uniteus' ? false : true }}
              data-cy="referral-form-member-first-name"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Member's Last Name</span>}
              model="memberLastName"
              onChange={handleLastNameValidation}
              onBlur={handleLastNameValidation}
              isDisabled={vendor=='uniteus' ? true : false}
              validators={{ required: vendor=='uniteus' ? false : true }}
              data-cy="referral-form-member-last-name"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <TextInput
              label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Member's Phone</span>}
              model="memberPhone"
              onChange={handlePhoneNumberValidation}
              onBlur={handlePhoneNumberValidation}
              placeholder="(___) ___-____"
              mask="phone"
              isDisabled={vendor=='uniteus' ? true : false}
              // Becomes required if the user selects "phone" as a contact preference
              validators={
                referralForm.getValues('contactPreferences').includes('phone') ||
                referralForm.getValues('contactPreferences').includes('text')
                  ? { required: 'Required' }
                  : { required: false }
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Member's Email</span>}
              model="memberEmail"
              onChange={handleEmailAddressValidation}
              onBlur={handleEmailAddressValidation}
              isDisabled={vendor=='uniteus' ? true : false}
              // Becomes required if the user selects "email" as a contact preference
              validators={
                vendor === 'uniteus'
                  ? { required: false }
                  : referralForm.getValues('contactPreferences').includes('email')
                  ? { required: 'Required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' } }
                  : { required: false, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' } }
              }
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'flex-start' }}>
          {/*
          If this is a findhelp referral, primary need will be a single-select dropdown.
        */}
          <div style={{ flex: '0 0 calc(50% - 5px)' }}>
            <SelectInput
              label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Primary Need</span>}
              placeholder="Select primary need"
              options={needSelectOptions as any[]}
              onChange={handlePrimaryReasonChange}
              onBlur={handlePrimaryReasonValidation}
              validators={{ required: 'Required' }}
              model="selectedNeeds.primaryNeeds"
            />
          </div>

          {/*
          If this is a findhelp referral, secondary need will be a multi-select dropdown.
        */}
          {vendor === 'findhelp' && (
            <div style={{ flex: 1 }}>
              <SelectInputMulti
                label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Secondary Need</span>}
                placeholder="Select secondary need"
                isMulti
                options={filterOutPrimarySelected(
                  referralForm.getValues('selectedNeeds.primaryNeeds')?.toString(),
                  needSelectOptions
                ) as any[]}
                validators={{ required: false }}
                model="selectedNeeds.secondaryNeeds"
              />
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
};

const ReferralFormMemberContactPreferences = ({
  referralForm,
}: {
  referralForm: ReturnType<typeof useForm<ReferralFormData>>;
}) => {
  const handleContactPreferenceValidation = () => {
    referralForm.trigger('contactPreferences');
  };
  return (
    <FormProvider state={referralForm}>
      <div>
        <CheckboxGroup
          label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Best way to reach the member</span>}
          css={{
            'abyss-checkbox-root': {
              paddingRight: '16px',
            },
            'abyss-checkbox-group-label-container': {
              paddingLeft: '0',
            },
            'abyss-checkbox-group-container': {
              paddingLeft: '0',
              // display:'grid',
            },
          }}
          model="contactPreferences"
          onChange={handleContactPreferenceValidation}
          validators={{
            required: 'Required',
          }}
        >
          <Checkbox
            value="phone"
            label="Phone call"
            css={{
              'abyss-checkbox-root': {
                paddingRight: '16px',
              },
            }}
            onChange={(_e: BaseSyntheticEvent) => {
              if (_e.target.checked) {
                // Checking an actual contact preference removes "do not contact" from the
                //  array of preferences.
                if (
                  referralForm
                    .getValues('contactPreferences')
                    .includes('do not contact')
                ) {
                  const updated = referralForm.getValues('contactPreferences');
                  referralForm.setValue(
                    'contactPreferences',
                    updated.filter((pref: string) => pref !== 'do not contact')
                  );
                }
              } else {
                // Phone unchecked — clear phone error if text is also not selected
                if (!referralForm.getValues('contactPreferences').includes('text')) {
                  referralForm.clearErrors('memberPhone');
                }
              }
            }}
          />
          <Checkbox
            value="text"
            label="Text message"
            css={{
              'abyss-checkbox-root': {
                paddingRight: '16px',
              },
            }}
            onChange={(_e: BaseSyntheticEvent) => {
              // Checking an actual contact preference removes "do not contact" from the actual
              //  array of preferences.
              if (_e.target.checked) {
                if (
                  referralForm
                    .getValues('contactPreferences')
                    .includes('do not contact')
                ) {
                  const updated = referralForm.getValues('contactPreferences');
                  referralForm.setValue(
                    'contactPreferences',
                    updated.filter((pref: string) => pref !== 'do not contact')
                  );
                }
              } else {
                // Text unchecked — clear phone error if phone is also not selected
                if (!referralForm.getValues('contactPreferences').includes('phone')) {
                  referralForm.clearErrors('memberPhone');
                }
              }
            }}
          />
          <Checkbox
            value="email"
            label="E-mail"
            css={{
              'abyss-checkbox-root': {
                paddingRight: '16px',
              },
            }}
            onChange={(_e: BaseSyntheticEvent) => {
              if (_e.target.checked) {
                if (
                  referralForm
                    .getValues('contactPreferences')
                    .includes('do not contact')
                ) {
                  const updated = referralForm.getValues('contactPreferences');
                  referralForm.setValue(
                    'contactPreferences',
                    updated.filter((pref: string) => pref !== 'do not contact')
                  );
                }
              } else {
                // Email unchecked — clear email error
                referralForm.clearErrors('memberEmail');
              }
            }}
          />
          <Checkbox
            label="Don't reach out"
            value="do not contact"
            onChange={(e: BaseSyntheticEvent) => {
              if (e.target.checked) {
                // If this bubble just got checked,
                //  remove all other preferences and push "do not contact".
                referralForm.setValue('contactPreferences', ['do not contact']);
                referralForm.clearErrors('memberPhone');
                referralForm.clearErrors('memberEmail');
              } else {
                // If we unchecked this bubble, there should be zero preferences selected
                referralForm.setValue('contactPreferences', []);
              }
            }}
          />
        </CheckboxGroup>
      </div>
    </FormProvider>
  );
};

const ReferralFormMemberContactPreferences_UniteUS = ({
  referralForm,
}: {
  referralForm: ReturnType<typeof useForm<ReferralFormData>>;
}) => {
  const handleContactPreferenceValidation = () => {
    referralForm.trigger('contactPreferences');
  };
  return (
    <FormProvider state={referralForm}>
      <div>
        <CheckboxGroup
          label={<span style={{ fontWeight: 'bold', color: '$core.color.neutral.100' }}>Best way to reach the member</span>}
          css={{
            'abyss-checkbox-root': {
              paddingRight: '16px',
            },
            'abyss-checkbox-group-label-container': {
              paddingLeft: '0',
            },
            'abyss-checkbox-group-container': {
              paddingLeft: '0',
              // display:'grid',
            },
          }}
          model="contactPreferences"
          onChange={handleContactPreferenceValidation}
          validators={{
            required: 'Required',
          }}
        >
          <CheckboxGroup.Column>
            <Checkbox
              value="I want someone to contact me by phone regarding this request"
              label="I want someone to contact me by phone regarding this request"
              css={{
                'abyss-checkbox-root': {
                  paddingRight: '16px',
                },
              }}
              onChange={(_e: BaseSyntheticEvent) => {
                // Checking an actual contact preference removes "do not contact" from the
                //  array of preferences.
                if (
                  referralForm
                    .getValues('contactPreferences')
                    .includes(
                      'I do not want to be contacted and would like the information to contact the resource on my own'
                    )
                ) {
                  const updated = referralForm.getValues('contactPreferences');
                  referralForm.setValue(
                    'contactPreferences',
                    updated.filter((pref: string) => {
                      return (
                        pref !==
                        'I do not want to be contacted and would like the information to contact the resource on my own'
                      );
                    })
                  );
                }
              }}
            />
            <Checkbox
              value="I do not want to be contacted and would like the information to contact the resource on my own"
              label="I do not want to be contacted and would like the information to contact the resource on my own  "
              css={{
                'abyss-checkbox-root': {
                  paddingRight: '16px',
                },
              }}
              onChange={(e: BaseSyntheticEvent) => {
                if (e.target.checked) {
                  // If this bubble just got checked,
                  //  remove all other preferences and push "do not contact".
                  referralForm.setValue('contactPreferences', [
                    'I do not want to be contacted and would like the information to contact the resource on my own',
                  ]);
                } else {
                  // If we unchecked this bubble, there should be zero preferences selected
                  referralForm.setValue('contactPreferences', []);
                }
              }}
            />
          </CheckboxGroup.Column>
        </CheckboxGroup>
      </div>
    </FormProvider>
  );
};

const PaddedLineBreak = () => {
  return (
    <hr
      style={{
        marginTop: '16px',
        marginBottom: '8px',
      }}
    />
  );
};
